import { ComboBox, ComboBoxItem } from '@change/ds-forms';
import { LocationPinIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePlaceWithDetailsField } from '../hooks/usePlaceWithDetailsField';
import type { GeoScope, PlaceWithDetails } from '../shared/types';

import { GoogleMapsAttribution } from './GoogleMapsAttribution';

export type PlaceWithDetailsFieldProps = {
	label: string;
	placeholder?: string;
	hideLabel?: boolean;
	geoScope?: GeoScope;
	placeInputValue: PlaceWithDetails | null;
	onChange: (place: PlaceWithDetails | null) => void;
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onSelectionChangeTracking?: (selectedPlaceId: string, placeTypes: readonly string[]) => void;
	onError?: (error: Error) => void;
	errorMessage?: string;
	size?: 'small' | 'large';
	inputLengthApiCallThreshold?: number;
	['data-qa']?: string;
	['data-testid']?: string;
};

const PlaceWithDetailsFieldComponent: React.FC<PlaceWithDetailsFieldProps> = ({
	geoScope,
	placeInputValue,
	label,
	placeholder,
	hideLabel = false,
	onChange,
	onFocus,
	onBlur,
	onSelectionChangeTracking,
	onError,
	errorMessage,
	size,
	inputLengthApiCallThreshold,
	...props
}) => {
	const { translate } = useI18n();

	const { places, isLoading, onSelectionChange, onInputChange } = usePlaceWithDetailsField({
		geoScope,
		placeInputValue,
		onChange,
		onError,
		inputLengthApiCallThreshold,
	});

	return (
		<ComboBox
			className="h-[52px]"
			name="petition-place"
			aria-label={label || translate('corgi.components.place.label')}
			label={label || translate('corgi.components.place.label')}
			placeholder={placeholder}
			labelIsVisible={!hideLabel}
			showCaretDownIcon={false}
			allowsCustomValue
			items={places}
			inputValue={placeInputValue?.displayName || ''}
			onSelectionChange={async (selectedPlaceId) => {
				await onSelectionChange(selectedPlaceId as string);

				if (onSelectionChangeTracking && selectedPlaceId) {
					const selectedPlace = places.find((place) => place.googlePlacesId === selectedPlaceId);
					onSelectionChangeTracking(selectedPlaceId as string, selectedPlace?.placePrediction?.types ?? []);
				}
			}}
			onInputChange={onInputChange}
			onFocus={onFocus}
			onBlur={onBlur}
			// TODO add missing translation keys for loading and empty state
			renderEmptyState={() => <div>{isLoading ? 'Loading...' : 'No options'}</div>}
			allowsEmptyCollection
			errorMessage={errorMessage}
			isInvalid={!!errorMessage}
			menuTrigger="input"
			size={size}
			footer={<GoogleMapsAttribution />}
			{...props}
		>
			{(place) => (
				<ComboBoxItem
					id={place.googlePlacesId}
					label={place.displayName}
					key={place.googlePlacesId}
					leadingIcon={<LocationPinIcon />}
				/>
			)}
		</ComboBox>
	);
};

export const PlaceWithDetailsField = PlaceWithDetailsFieldComponent;
